import type * as React from 'react'
import { Bell } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { NAV_SECTIONS } from '@/components/AppSidebar'

function labelFor(url: string) {
  const match = NAV_SECTIONS.flatMap((s) => s.items)
    .filter((item) => url.startsWith(item.activePrefix ?? item.href))
    .sort((a, b) => (a.activePrefix ?? a.href).length - (b.activePrefix ?? b.href).length)
    .pop()
  return match?.label ?? 'Admin'
}

interface AppTopbarProps {
  url: string
  unreadCount: number
  environment?: string
  header?: React.ReactNode
  headerActions?: React.ReactNode
}

export function AppTopbar({
  url,
  unreadCount,
  environment,
  header,
  headerActions,
}: AppTopbarProps) {
  return (
    <header
      className={cn(
        'border-border sticky top-0 z-30 flex h-14 items-center gap-3 border-b px-10',
        'bg-bg/80 backdrop-blur-md',
      )}
    >
      {header ?? (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs">
          <span className="text-fg-muted">Admin</span>
          <span aria-hidden className="text-fg-subtle">
            /
          </span>
          <span aria-current="page" className="text-fg">
            {labelFor(url)}
          </span>
        </nav>
      )}

      <div className="ml-auto flex items-center gap-2">
        {headerActions}
        {/* Which environment you are looking at is global state, so it sits with
            the other global state rather than under the product name — where it
            also collided with the nav section of the same word. */}
        {environment && environment !== 'production' && (
          <Badge variant="warning" size="tag" dot className="capitalize">
            {environment}
          </Badge>
        )}
        <Button variant="outline" size="icon" aria-label="Notifications" className="relative">
          <Bell />
          {unreadCount > 0 && (
            <span className="bg-err text-bg ring-bg absolute -top-1 -right-1 grid min-w-4.5 place-items-center rounded-full px-1 text-2xs font-bold ring-2">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </div>
    </header>
  )
}
